<template>
  <div class="d-flex flex-column justify-content-end w-100" v-if="!itemModel.vars.hide">
    <label
      v-if="itemModel.vars.label"
      :id="labelId"
      :for="answerModel?.vars?.id"
      :class="'form-label' + (itemModel.vars.required ? ' required' : '')"
    >
      {{ itemModel.vars.label }}{{ itemModel.vars.required ? "*" : "" }}
    </label>
    <div class="captcha">
      <div class="captcha-q">
        <span :id="questionId">{{ questionLabel }}</span>
        <input
          v-if="answerModel"
          :id="answerModel.vars.id"
          v-model="answerModel.vars.value"
          type="text"
          inputmode="numeric"
          pattern="[0-9]*"
          :class="computedClass"
          :name="answerModel.vars.name"
          :disabled="answerModel.vars.disabled === true || loadingReload"
          :required="answerModel.vars.required === true"
          :aria-labelledby="ariaLabelledby"
          :aria-describedby="answerModel.vars.error ? errorId : null"
          :aria-invalid="answerModel.vars.error ? 'true' : null"
          @blur="blur"
          @input="input"
        />
      </div>
      <button v-if="reloadUrl" type="button" class="captcha-refresh" :disabled="loadingReload" @click="reload">
        <FontAwesomeIcon icon="fa-light fa-rotate" :spin="loadingReload" />
        {{ $t("forms.captcha.new_calculation") }}
      </button>
    </div>
    <div v-if="answerModel && answerModel.vars.error" :id="errorId" class="invalid-feedback d-block" role="alert">
      {{ answerModel.vars.error }}
    </div>
  </div>
</template>

<script>
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { formManager } from "@/services/form/form-manager";
import { formValidator } from "@/services/form/form-validator";
import { apiConnection } from "@/services/api-connection";
import { notificationManager } from "@/services/utilities/notification-manager";

// Branché dans le pipeline de rendu générique via formManager.isSumCaptchaType() (block_prefix
// "sum_captcha", confirmé côté back dans SumCaptchaType::getBlockPrefix()) + ModelComponent.vue.
// TODO(ST304-TA-4550) : `reloadUrl` n'est pas encore transmis par ModelComponent.vue (aucune vue
// ne lui fait actuellement suivre l'URL du GET .../form ayant chargé le formulaire parent - cf.
// prop reloadUrl ci-dessous). Sans plomberie dédiée, le bouton "Nouveau calcul" reste simplement
// masqué (cf. `v-if="reloadUrl"` dans le template) : dégradation gracieuse, pas bloquant pour
// l'affichage/la validation du captcha. À faire une fois qu'une vue consommatrice concrète existe.
export default {
  name: "SumCaptchaTypeComponent",
  components: { FontAwesomeIcon },
  props: {
    model: {
      type: Object,
      required: true,
    },
    // URL du GET .../form (celle déjà utilisée pour charger le formulaire parent) permettant de
    // régénérer firstNumber/secondNumber/token - pas d'endpoint de refresh dédié côté back.
    reloadUrl: {
      type: String,
      required: false,
      default: "",
    },
  },
  emits: ["draft", "update"],
  data() {
    return {
      // Même référence que la prop `model` (pas de clone) : c'est le pattern déjà en place sur tous
      // les *TypeComponent.vue de ce dossier - le formulaire est un arbre unique construit par
      // formManager et partagé par tous les champs, mutable directement, pour que le parent
      // (FormComponent) lise l'état à jour sans prop-drilling ni ré-émission explicite à chaque
      // frappe. Ne pas cloner ici casserait cette convention et désynchroniserait ce champ du reste
      // du formulaire.
      // Vérifié (pas juste supposé) : même pattern data() { itemModel: this.model } + mutation
      // directe de itemModel.vars.value dans InputTypeComponent.vue, SwitchTypeComponent.vue,
      // ColorPickerComponent.vue, TextareaTypeComponent.vue, CheckboxTypeComponent.vue et
      // RadioTypeComponent.vue.
      itemModel: this.model,
      loadingReload: false,
    };
  },
  computed: {
    firstNumberModel() {
      return formManager.findFieldByName(this.itemModel.children, "firstNumber");
    },
    secondNumberModel() {
      return formManager.findFieldByName(this.itemModel.children, "secondNumber");
    },
    // token n'est jamais lu/affiché/modifié ici : opaque côté front, seulement reçu puis
    // réémis tel quel via le payload générique construit par formManager.processChildren.
    answerModel() {
      return formManager.findFieldByName(this.itemModel.children, "answer");
    },
    questionLabel() {
      return `${this.firstNumberModel?.vars?.value ?? ""} + ${this.secondNumberModel?.vars?.value ?? ""} =`;
    },
    // Nom accessible de l'input : la question ("7 + 4 =") est la seule information utile pour
    // répondre, le label statique ("Captcha") n'est qu'un intitulé de champ générique optionnel.
    questionId() {
      return this.answerModel ? `${this.answerModel.vars.id}_question` : null;
    },
    labelId() {
      return this.itemModel.vars.label && this.answerModel ? `${this.answerModel.vars.id}_label` : null;
    },
    ariaLabelledby() {
      return [this.labelId, this.questionId].filter(Boolean).join(" ") || null;
    },
    errorId() {
      return this.answerModel ? `${this.answerModel.vars.id}_error` : null;
    },
    computedClass() {
      if (this.answerModel?.vars?.error || this.answerModel?.vars?.isInvalid) {
        return "form-control is-invalid";
      }
      return "form-control";
    },
  },
  watch: {
    model() {
      this.itemModel = this.model;
    },
  },
  methods: {
    input() {
      if (this.answerModel) {
        // Clavier desktop : inputmode/pattern n'empêchent pas la saisie de caractères non
        // numériques, on les filtre donc explicitement avant toute validation.
        const digitsOnly = (this.answerModel.vars.value ?? "").replace(/[^0-9]/g, "");
        if (digitsOnly !== this.answerModel.vars.value) {
          this.answerModel.vars.value = digitsOnly;
        }
      }
      formValidator.validModel(this.answerModel);
      this.$emit("draft", this.itemModel);
    },
    blur() {
      this.input();
      this.$emit("update", this.answerModel?.vars?.value);
    },
    async reload() {
      if (!this.reloadUrl || this.loadingReload) {
        return;
      }
      this.loadingReload = true;
      try {
        const schema = await apiConnection.get(this.reloadUrl);
        const fresh = formManager.findFieldByName(schema, this.itemModel.vars.name);
        if (!fresh) {
          // Échec silencieux sinon : la réponse n'a pas la forme attendue (pas de wrapper .data/.form
          // par exemple), ou le nom du champ ne correspond plus à celui du formulaire chargé.
          console.error(
            `SumCaptchaTypeComponent.reload() : champ "${this.itemModel.vars.name}" introuvable dans la réponse de ${this.reloadUrl} - aucune valeur mise à jour.`,
          );
          notificationManager.showNotification("error", this.$t("forms.captcha.reload_error"));
          return;
        }
        ["firstNumber", "secondNumber", "token"].forEach((name) => {
          const freshChild = formManager.findFieldByName(fresh.children, name);
          const currentChild = formManager.findFieldByName(this.itemModel.children, name);
          if (freshChild && currentChild) {
            currentChild.vars.value = freshChild.vars.value;
          }
        });
        if (this.answerModel) {
          this.answerModel.vars.value = "";
          this.answerModel.vars.error = null;
          this.answerModel.vars.isInvalid = false;
        }
        this.$emit("draft", this.itemModel);
      } catch (error) {
        console.error("SumCaptchaTypeComponent.reload() a échoué :", error);
        notificationManager.showNotification("error", this.$t("forms.captcha.reload_error"));
      } finally {
        this.loadingReload = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.captcha {
  border: 1px solid $m-color_4;
  border-radius: 6px;
  padding: 11px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 9px;

  @include dark-theme {
    border-color: $m-color_12;
  }
}

.captcha-q {
  display: flex;
  align-items: center;
  gap: 9px;
  font-weight: 700;

  input {
    width: 60px;
    text-align: center;
  }
}

.captcha-refresh {
  background: none;
  border: none;
  color: $dark-grey;
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;

  @include dark-theme {
    color: $light-grey;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
}
</style>
